import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Table, Header, HeaderCell, Body, Row, Cell } from '@calendly/table';
import { CheckboxInput } from '@calendly/ui/components/checkbox-input';
import { CheckCircleIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { Pagination } from '@calendly/ui/components/pagination';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorSuccess } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useState, useEffect, useRef, forwardRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setGetParams } from 'actions/core/navigation';
import { useGetUserQuery } from 'common';
import LoadingState from 'components/core/loading_state';
import { ManagingBadge } from 'components/core/managing_badge';
import { ShowOnly } from 'components/core/show_only';
import { useSettingsModalQueryParams } from 'components/use_settings_modal_query_params';
import { isAdminOrOwnerSelector } from 'selectors/current_user';
import { ActionsDropdown } from './actions_dropdown';
import { PAGE_SIZE, useFetchBookingVerificationSettingsQuery, useCreateBookingVerificationSettingsMutation, useDeleteBookingVerificationSettingsMutation } from './api';
import { EventSelectionControls } from './batch_controls';
import { SearchFilter } from './search_filter';
import { setSelectedEventTypes, selectedEventTypesSelector } from './slice';
const markerCss = "m1wb57of";
const Marker = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    color,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  const style = {
    backgroundColor: color
  };
  return <Component ref={ref} {...restProps} style={style} data-component="marker" className={classNames(markerCss, props.className)} />;
});
const nameWrapperCss = "n308qa0";
const NameWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(nameWrapperCss, props.className)} />;
});
const emailVerificationStatusCss = "eusfge0";
const EmailVerificationStatus = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emailVerificationStatusCss, props.className)} />;
});
const paginationContainerCss = "peqjby2";
const PaginationContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(paginationContainerCss, props.className)} />;
});
const HELP_ARTICLE_URL = 'https://calendly.com/help/how-to-require-email-verification-for-event-types';
const HelpCenterLink = ({
  children
}) => <Link href={HELP_ARTICLE_URL} target="_blank" rel="noopener noreferrer">
    {children}
  </Link>;
const EventTypeCheckbox = ({
  eventType
}) => {
  const dispatch = useDispatch();
  const selectedEventTypes = useSelector(selectedEventTypesSelector);
  const selectEvent = selectedEvent => {
    dispatch(setSelectedEventTypes({
      uuid: selectedEvent.uuid,
      id: selectedEvent.id
    }));
  };
  return <div>
      <label>
        <CheckboxInput checked={selectedEventTypes[eventType.uuid] || false} onChange={() => selectEvent(eventType)} aria-label={eventType.name} />
      </label>
    </div>;
};

/** Team/user group fields may be a string or `{ id, name, uuid }` from the API. */
const getOwningResource = value => {
  if (value == null) return undefined;
  if (typeof value === 'string') return value;
  return value.name;
};
const EventTypeSettingsTableBody = ({
  userId,
  eventTypes,
  refetch
}) => <Body>
    {eventTypes.map(eventType => <Row key={eventType.id}>
        <Cell label={I18n.t('publisher.organization_settings.booking_verification.event_type.name')}>
          <NameWrapper>
            <ShowOnly on="isDesktopUp">
              <EventTypeCheckbox eventType={eventType} />
            </ShowOnly>
            <Marker color={eventType.color} />
            <BodyText weight="bold" inline>{eventType.name}</BodyText>
            {eventType.type === 'ManagedEventType' && <ManagingBadge userGroupManaged={!!eventType.owningUserGroup} />}
          </NameWrapper>
        </Cell>
        <Cell label={I18n.t('publisher.organization_settings.booking_verification.event_type.verification')}>
          {eventType.emailVerificationEnabled && <EmailVerificationStatus>
              <CheckCircleIcon size="large" color={colorSuccess} />
              {I18n.t('publisher.organization_settings.booking_verification.email_verification_enabled')}
            </EmailVerificationStatus>}
        </Cell>
        <Cell label={I18n.t('publisher.organization_settings.booking_verification.event_type.kind')}>
          {eventType.kindDescription}
        </Cell>
        <Cell label={I18n.t('publisher.organization_settings.booking_verification.event_type.owned_by')}>
          {getOwningResource(eventType.owningUserName) ?? getOwningResource(eventType.owningTeam) ?? getOwningResource(eventType.owningUserGroup) ?? I18n.t('publisher.organization_settings.booking_verification.organization_owner')}
        </Cell>
        <Cell label={I18n.t('publisher.organization_settings.booking_verification.event_type.owning_team')}>
          {getOwningResource(eventType.owningTeam)}
        </Cell>
        <Cell label={I18n.t('publisher.organization_settings.booking_verification.event_type.last_edited')}>
          {eventType.briefUpdatedAt}
        </Cell>
        <ShowOnly on="isTabletDown">
          <Cell hasActions>
            <ActionsDropdown userId={userId} eventType={eventType} refetch={refetch} />
          </Cell>
        </ShowOnly>
      </Row>)}
  </Body>;
const descriptionIsTabletDownCss = "d1hfeo4b";
const descriptionCss = "d1y9xbqs";
const Description = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTabletDown = useDeviceType('isTabletDown');
  return <Component ref={ref} {...restProps} className={classNames(descriptionCss, {
    [descriptionIsTabletDownCss]: isTabletDown
  }, props.className)} />;
});
export const BookingVerificationSettings = () => {
  const dispatch = useDispatch();
  const {
    page: initialPage,
    sortField: initSortField,
    order: initSortOrder,
    filter_term: filterTerm,
    host_ids: hostIds,
    owner_ids: ownerIds,
    permission
  } = useSettingsModalQueryParams();
  const [page, setPage] = useState(parseInt(initialPage, 10) || 1);
  const [sortField, setSortField] = useState(initSortField || 'name');
  const [sortOrder, setSortOrder] = useState(initSortOrder || 'asc');
  const {
    data: {
      id: userId
    }
  } = useGetUserQuery();
  const isAdmin = useSelector(isAdminOrOwnerSelector);
  const [createBookingVerificationSettings] = useCreateBookingVerificationSettingsMutation();
  const [deleteBookingVerificationSettings] = useDeleteBookingVerificationSettingsMutation();
  const eventTypeSettings = useFetchBookingVerificationSettingsQuery({
    userId,
    page,
    sortField,
    sortOrder,
    filterTerm,
    hostIds,
    ownerIds,
    permission,
    scope: isAdmin ? 'booking_verification_admin' : 'booking_verification_profile'
  }, {
    refetchOnMountOrArgChange: true
  });
  const isLoading = eventTypeSettings.status === 'pending';
  const paginationData = eventTypeSettings.data?.pagination ?? {};
  const {
    totalCount
  } = paginationData;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const sortedColumn = {
    name: sortField,
    direction: sortOrder
  };

  // Track previous filter values to detect when they change
  const prevFiltersRef = useRef({
    filterTerm,
    hostIds,
    ownerIds,
    permission
  });

  // Reset page to 1 only when search/filter parameters actually change
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const hasFilterChanged = prevFilters.filterTerm !== filterTerm || prevFilters.hostIds !== hostIds || prevFilters.ownerIds !== ownerIds || prevFilters.permission !== permission;
    if (hasFilterChanged) {
      setPage(1);
      dispatch(setGetParams({
        page: 1
      }));
    }

    // Update the ref with current values
    prevFiltersRef.current = {
      filterTerm,
      hostIds,
      ownerIds,
      permission
    };
  }, [filterTerm, hostIds, ownerIds, permission, dispatch]);
  const updatePage = nextPage => {
    if (nextPage >= 1 && nextPage <= totalPages) {
      setPage(nextPage);
      dispatch(setGetParams({
        page: nextPage
      }));
    } else {
      setPage(1);
      dispatch(setGetParams({
        page: 1
      }));
    }
  };
  const sortEventTypes = column => {
    if (sortField === column) {
      setSortOrder(prevSortOrder => prevSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortOrder('asc');
    }
    setSortField(column);
  };
  return <>
      {isLoading && <LoadingState />}
      {!isLoading && <>
          <Description>
            {HtmlParser(I18n.t('publisher.organization_settings.booking_verification.description'), {
          HelpCenterLink
        })}
          </Description>
          <SearchFilter />
          <EventSelectionControls userId={userId} createBookingVerificationSettings={createBookingVerificationSettings} deleteBookingVerificationSettings={deleteBookingVerificationSettings} refetchEventTypeSettings={eventTypeSettings.refetch} />
          <Table sortedColumn={sortedColumn} onSort={sortEventTypes}>
            <Header>
              <HeaderCell columnName="name">
                {I18n.t('publisher.organization_settings.booking_verification.event_type.name')}
              </HeaderCell>
              <HeaderCell columnName="verification">
                {I18n.t('publisher.organization_settings.booking_verification.event_type.verification')}
              </HeaderCell>
              <HeaderCell>
                {I18n.t('publisher.organization_settings.booking_verification.event_type.kind')}
              </HeaderCell>
              <HeaderCell>
                {I18n.t('publisher.organization_settings.booking_verification.event_type.owned_by')}
              </HeaderCell>
              <HeaderCell>
                {I18n.t('publisher.organization_settings.booking_verification.event_type.owning_team')}
              </HeaderCell>
              <HeaderCell columnName="last_edited">
                {I18n.t('publisher.organization_settings.booking_verification.event_type.last_edited')}
              </HeaderCell>
            </Header>
            <EventTypeSettingsTableBody userId={userId} eventTypes={eventTypeSettings.data?.results || []} refetch={eventTypeSettings.refetch} />
          </Table>
          <PaginationContainer>
            <Pagination currentPage={page} totalPages={totalPages} totalCount={totalCount} onPageClick={updatePage} pageSize={PAGE_SIZE} />
          </PaginationContainer>
        </>}
    </>;
};

require("./settings.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./settings.js");